import { useEffect, useMemo } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useDeviceStore } from '@/stores/useDeviceStore';
import { fetchDevices } from '@/services/devices';
import { getDeviceStatus } from '@/lib/utils';
import { LOG_PREFIX } from '@/config/constants';
import type { Device } from '@/types';

export function useDevices() {
  const organizationIds = useAuthStore((s) => s.getOrganizationIds());
  const { devices, filters, setDevices } = useDeviceStore();

  useEffect(() => {
    if (organizationIds.length === 0) return;

    const load = async () => {
      try {
        const data = await fetchDevices(organizationIds);
        setDevices(data);
      } catch (err) {
        console.error(LOG_PREFIX, 'Failed to fetch devices:', err);
      }
    };

    load();
  }, [organizationIds, setDevices]);

  const filteredDevices = useMemo(() => {
    let result: Device[] = devices;

    result = result.filter((d) =>
      filters.showArchived ? d.archived_at != null : d.archived_at == null
    );

    if (filters.search) {
      const search = filters.search.toLowerCase();
      const searchDigits = search.replace(/\D/g, '');
      result = result.filter((d) => {
        if (d.device_id.toLowerCase().includes(search)) return true;
        if (d.device_name.toLowerCase().includes(search)) return true;
        if (d.serial_number && d.serial_number.toLowerCase().includes(search)) return true;
        if (searchDigits && d.phone_number) {
          const phoneDigits = d.phone_number.replace(/\D/g, '');
          if (phoneDigits.includes(searchDigits)) return true;
        }
        return false;
      });
    }

    if (filters.status !== 'all') {
      result = result.filter((d) => {
        const status = getDeviceStatus(d.last_checkin_at);
        return status === filters.status;
      });
    }

    if (filters.organizationId !== 'all') {
      result = result.filter((d) => d.organization_id === filters.organizationId);
    }

    return result;
  }, [devices, filters]);

  return { devices: filteredDevices, allDevices: devices };
}
